import React, { FC } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Button,
  Column,
  Grid,
  HorizontalLine,
  Stack,
  Text,
  useBreakpoint,
  UserCard,
} from '@components'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersEditorsSection_guild$key } from './__generated__/GuildMembersEditorsSection_guild.graphql'

export const GuildMembersEditorsSection: FC<{
  guild: GuildMembersEditorsSection_guild$key
}> = ({ guild }) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersEditorsSection_guild on Guild
      @refetchable(queryName: "GuildMembersEditorsSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 3 }
        cursor: { type: "Cursor" }
      ) {
        editors(first: $count, after: $cursor)
          @connection(key: "GuildMembersEditorsSection_guild_editors") {
          edges {
            node {
              ...UserCard_user
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const breakpoint = useBreakpoint()

  if (data.editors.edges.length === 0) {
    return null
  }

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        <Stack direction='horizontal' gap={4} alignItems='center'>
          <Text size={4} variant='cardHeading'>
            Editors
          </Text>

          <HorizontalLine variant='cardHeading' />
        </Stack>

        {data.editors.edges.map((edge) => (
          <UserCard
            key={edge.cursor}
            user={edge.node}
            limitDescriptionLines={4}
          />
        ))}

        {hasNext && (
          <Button
            variant='secondary'
            hoverVariant='secondaryHover'
            onPress={() => {
              if (isLoadingNext) {
                return
              }

              loadNext(3)
            }}
          >
            <Text variant='buttonSecondary' size={4}>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Text>
          </Button>
        )}
      </Stack>
    )
  }

  const editorGroups = data.editors.edges.reduce<
    Array<Array<(typeof data.editors.edges)[number]>>
  >((memo, organizer, index) => {
    if (index % INITIAL_COUNTS[breakpoint] === 0) {
      memo.push([organizer])
    } else {
      memo[memo.length - 1].push(organizer)
    }

    return memo
  }, [])

  return (
    <Stack gap={4}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Editors
        </Text>

        <HorizontalLine variant='cardHeading' />
      </Stack>

      <Stack gap={4}>
        {editorGroups.map((group, index) => {
          return (
            <Stack key={index}>
              <Grid gap={4}>
                {group.map((edge) => (
                  <Column key={edge.cursor}>
                    <UserCard
                      user={edge.node}
                      limitDescriptionLines={4}
                      expand
                    />
                  </Column>
                ))}

                {INITIAL_COUNTS[breakpoint] - group.length > 0 ? (
                  <Column span={INITIAL_COUNTS[breakpoint] - group.length} />
                ) : null}
              </Grid>
            </Stack>
          )
        })}
      </Stack>

      {hasNext ? (
        <Button
          variant='secondary'
          hoverVariant='secondaryHover'
          onPress={() => {
            if (isLoadingNext) {
              return
            }

            loadNext(3)
          }}
        >
          <Text variant='buttonSecondary'>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Text>
        </Button>
      ) : null}
    </Stack>
  )
}
